'use client';

import { useEffect } from 'react';

type ErrorPayload = {
    source: string;
    message: string;
    stack?: string;
    buildId?: string | null;
    timestamp: string;
    details?: Record<string, unknown>;
};

function logGlobalError(payload: ErrorPayload) {
    try {
        console.error('[GLOBAL_ERROR]', JSON.stringify(payload));
    } catch {
        console.error('[GLOBAL_ERROR]', payload);
    }
}

const GlobalErrorObserver = () => {
    useEffect(() => {
        const getBuildId = () => {
            if (typeof window === 'undefined') return null;
            const data = window.__NEXT_DATA__?.buildId ?? null;
            return typeof data === 'string' ? data : null;
        };

        const handleErrorEvent = (event: ErrorEvent) => {
            const payload: ErrorPayload = {
                source: 'window.error',
                message: event?.message || 'unknown error',
                stack: event?.error?.stack,
                buildId: getBuildId(),
                timestamp: new Date().toISOString(),
                details: {
                    filename: event?.filename,
                    lineno: event?.lineno,
                    colno: event?.colno,
                    isChunkLoadError: /ChunkLoadError|Loading chunk \d+ failed/i.test(event?.message || ''),
                },
            };
            logGlobalError(payload);
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event?.reason;
            const message =
                typeof reason === 'string'
                    ? reason
                    : reason?.message || 'unhandled rejection';
            const payload: ErrorPayload = {
                source: 'window.unhandledrejection',
                message,
                stack: reason?.stack,
                buildId: getBuildId(),
                timestamp: new Date().toISOString(),
                details: {
                    isChunkLoadError: /ChunkLoadError|Loading chunk \d+ failed/i.test(message),
                    reasonType: reason?.constructor?.name,
                },
            };
            logGlobalError(payload);
        };

        window.addEventListener('error', handleErrorEvent);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleErrorEvent);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    return null;
};

export default GlobalErrorObserver;

